import Head from 'expo-router/head';
import { ScrollView } from 'react-native';
import { getTokenValue, Text, YStack } from 'tamagui';

import SettingsGroup from '@/components/SettingsGroup';

/**
 * ── /components/settings-group ───────────────────────────────────────────
 *
 * SettingsGroup — the settings-page card of stacked, connected rows
 * (`app/settings.tsx`, reached from the profile screen's header cog). Rows
 * carry their OWN corner rounding (first row rounds top, last rounds
 * bottom) rather than a clipping parent, so a group reads as one recessed
 * `$backgroundSecondary` card with hairline dividers between rows and no
 * height loss on the last one — see the component's own docstring.
 *
 * Covers: a titled multi-row group, an untitled ALL-destructive group (the
 * real logout/delete-account block), a single-item group (both corners
 * round on the one row — `app/settings.tsx`'s current "Support" group, which
 * has drifted down to one row since this fixture was last written), a mixed
 * group pairing a destructive row with plain ones (no longer a real call
 * site, but a valid composition since `destructive` is per-item, not
 * per-group), and a row with no `onPress` (informational — its chevron
 * still renders, which is a gotcha worth seeing since it visually implies
 * tappability the row doesn't have).
 * ─────────────────────────────────────────────────────────────────────────
 */

export default function SettingsGroupPreview() {
  return (
    <>
      <Head>
        <title>SettingsGroup · Putting Scene</title>
      </Head>
      <ScrollView
        style={{
          flex: 1,
          backgroundColor: getTokenValue('$color.background', 'color'),
        }}
      >
        <YStack items="flex-start" gap="$lg" p="$lg">
          {/* Titled, multi-row group — matches settings.tsx's "Account"
            section verbatim (edit profile / your memberships), both rows
            live with onPress so the press-darken `pressStyle` is
            demonstrable. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              titled group — settings.tsx&apos;s &quot;Account&quot; section
            </Text>
            <YStack alignSelf="stretch">
              <SettingsGroup
                title="Account"
                items={[
                  {
                    icon: 'person-outline',
                    label: 'Edit profile',
                    onPress: () => {},
                  },
                  {
                    icon: 'diamond-outline',
                    label: 'Your memberships',
                    onPress: () => {},
                  },
                ]}
              />
            </YStack>
          </YStack>

          {/* Untitled, ALL-destructive group — settings.tsx's bottom-of-page
            block (Logout, Delete Account). No `title` at all: the group is
            just two red rows sitting under whatever's above it on the page.
            Both rows destructive shows the tint is purely per-row — nothing
            about the group itself turns "dangerous". */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              untitled, all-destructive — settings.tsx&apos;s trailing Logout /
              Delete Account block (no title prop passed)
            </Text>
            <YStack alignSelf="stretch">
              <SettingsGroup
                items={[
                  {
                    icon: 'log-out-outline',
                    label: 'Logout',
                    destructive: true,
                    onPress: () => {},
                  },
                  {
                    icon: 'trash-outline',
                    label: 'Delete Account',
                    destructive: true,
                    onPress: () => {},
                  },
                ]}
              />
            </YStack>
          </YStack>

          {/* Single-item group — both `first` and `last` land on the same
            row, so all four corners round on one card. settings.tsx's own
            "Support" group is down to exactly this (just "Contact us") as
            of this writing, even though the original all.tsx fixture this
            page supersedes gave "Support" three rows including a
            destructive one — the app has drifted since. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              single-item group — all four corners round on the one row;
              settings.tsx&apos;s current &quot;Support&quot; group
            </Text>
            <YStack alignSelf="stretch">
              <SettingsGroup
                title="Support"
                items={[
                  {
                    icon: 'chatbubble-ellipses-outline',
                    label: 'Contact us',
                    onPress: () => {},
                  },
                ]}
              />
            </YStack>
          </YStack>

          {/* Mixed group — a destructive row sitting inside an otherwise
            plain card. `destructive` is a per-item flag, not a per-group
            one, so nothing in the type stops this; no current screen does
            it (settings.tsx keeps its destructive rows isolated in their
            own untitled group above), but it's a valid composition and
            worth seeing: the red row's chevron stays neutral, same as
            every other row's, so only the icon+label read as dangerous. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              mixed group — a destructive row alongside plain ones (no real call
              site today, but a valid composition)
            </Text>
            <YStack alignSelf="stretch">
              <SettingsGroup
                title="About us"
                items={[
                  {
                    icon: 'shield-checkmark-outline',
                    label: 'Privacy policy',
                    onPress: () => {},
                  },
                  {
                    icon: 'document-text-outline',
                    label: 'Terms and conditions',
                    onPress: () => {},
                  },
                  {
                    icon: 'trash-outline',
                    label: 'Delete Account',
                    destructive: true,
                    onPress: () => {},
                  },
                ]}
              />
            </YStack>
          </YStack>

          {/* No `onPress` — `items` doesn't require it. The row still
            renders its trailing chevron unconditionally (SettingsRow has no
            branch that hides it), so a non-interactive row visually implies
            tappability it doesn't have; only `cursor` and the press-darken
            feedback are gated on `onPress` being present. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              no `onPress` — chevron still renders even though the row
              isn&apos;t tappable (cursor + press-darken are the only things
              gated on it)
            </Text>
            <YStack alignSelf="stretch">
              <SettingsGroup
                items={[
                  { icon: 'card-outline', label: 'Payment methods' },
                  { icon: 'ticket-outline', label: 'My bookings' },
                ]}
              />
            </YStack>
          </YStack>
        </YStack>
      </ScrollView>
    </>
  );
}
